import React, { useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command';
import { useCommandPalette } from '@/context/CommandPaletteContext';
import { useKeyboardShortcuts, formatShortcut } from '@/context/KeyboardShortcutsContext';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { Command, CommandCategory, ShortcutKey } from '@/types/keyboard-shortcuts';
import {
  Settings,
  Search,
  Home,
  HelpCircle,
  Zap,
  Edit3,
} from 'lucide-react';

const categoryConfig: Record<CommandCategory, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  navigation: { label: 'Navigation', icon: Home },
  actions: { label: 'Actions', icon: Zap },
  settings: { label: 'Settings', icon: Settings },
  help: { label: 'Help', icon: HelpCircle },
  search: { label: 'Search', icon: Search },
  editing: { label: 'Editing', icon: Edit3 },
};

const categoryOrder: CommandCategory[] = ['navigation', 'actions', 'settings', 'editing', 'search', 'help'];

// Keyboard shortcut display component with proper kbd styling
function ShortcutKeys({ keys, isMac }: { keys: ShortcutKey; isMac: boolean }) {
  const parts = formatShortcut(keys, isMac);

  return (
    <span className="ml-auto flex items-center gap-1">
      {parts.map((part, index) => (
        <kbd
          key={index}
          className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground"
        >
          {part}
        </kbd>
      ))}
    </span>
  );
}

type DashboardType = 'business' | 'employee' | 'portfolio' | null;

interface CurrentContext {
  dashboard: DashboardType;
  tab: string | null;
}

export function CommandPalette() {
  const { isOpen, close } = useCommandPalette();
  const { getAllCommands } = useKeyboardShortcuts();
  const location = useLocation();
  const {
    businessDashboardTab,
    employeeDashboardTab,
    franchiseDashboardTab,
  } = useDashboardTab();

  const commands = getAllCommands();

  // Get current context (dashboard and tab)
  const getCurrentContext = useCallback((): CurrentContext => {
    const path = location.pathname;
    let dashboard: DashboardType = null;
    let tab: string | null = null;

    if (path.startsWith('/dashboard')) {
      dashboard = 'business';
      tab = businessDashboardTab;
    } else if (path.startsWith('/employee-dashboard')) {
      dashboard = 'employee';
      tab = employeeDashboardTab;
    } else if (path.startsWith('/portfolio')) {
      dashboard = 'portfolio';
      tab = franchiseDashboardTab;
    }

    return { dashboard, tab };
  }, [location.pathname, businessDashboardTab, employeeDashboardTab, franchiseDashboardTab]);

  // Calculate relevance score for a command based on current context
  const getRelevanceScore = useCallback((command: Command, context: CurrentContext): number => {
    if (!command.context) return 0;

    let score = 0;
    const cmdContext = command.context;

    // Dashboard match: +2 points
    if (context.dashboard && cmdContext.dashboards?.includes(context.dashboard)) {
      score += 2;
    }

    // Tab match: +3 points (more specific = higher priority)
    if (context.tab && cmdContext.tabs?.includes(context.tab)) {
      score += 3;
    }

    return score;
  }, []);

  const groupedCommands = useMemo(() => {
    const groups = new Map<CommandCategory, Command[]>();
    const context = getCurrentContext();

    for (const command of commands) {
      if (command.showInPalette === false) continue;

      const category = command.category || 'actions';
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(command);
    }

    // Sort commands within each category by relevance, then alphabetically
    for (const [category, categoryCommands] of groups) {
      categoryCommands.sort((a, b) => {
        const scoreA = getRelevanceScore(a, context);
        const scoreB = getRelevanceScore(b, context);

        // Higher relevance first
        if (scoreA !== scoreB) {
          return scoreB - scoreA;
        }

        // Then alphabetically by label
        return a.label.localeCompare(b.label);
      });
    }

    return groups;
  }, [commands, getCurrentContext, getRelevanceScore]);

  const handleSelect = (command: Command) => {
    close();
    setTimeout(() => command.action(), 10);
  };

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);

  const sortedCategories = categoryOrder.filter((cat) => groupedCommands.has(cat));

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={(open) => !open && close()}
      contentClassName="max-w-2xl"
    >
      <CommandInput placeholder="Type a command or search..." />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>No results found.</CommandEmpty>

        {sortedCategories.map((category, index) => {
          const categoryCommands = groupedCommands.get(category)!;
          const config = categoryConfig[category];

          return (
            <React.Fragment key={category}>
              {index > 0 && <CommandSeparator />}
              <CommandGroup heading={config?.label || category}>
                {categoryCommands.map((command) => {
                  const Icon = command.icon || config?.icon;

                  return (
                    <CommandItem
                      key={command.id}
                      onSelect={() => handleSelect(command)}
                      keywords={command.keywords}
                    >
                      {Icon && <Icon className="mr-2 h-3.5 w-3.5 shrink-0" />}
                      <span className="truncate">{command.label}</span>
                      {command.description && (
                        <span className="ml-2 text-xs text-muted-foreground hidden md:inline truncate">
                          {command.description}
                        </span>
                      )}
                      {command.keys && <ShortcutKeys keys={command.keys} isMac={isMac} />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </React.Fragment>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
}
